/**
 * Live Mode — screen-local constants + pure helpers for the operator
 * "control-room" screen.
 *
 * This screen is deliberately built on different design principles than the
 * rest of the app: it is a passive, always-on, news-channel-style display.
 * The motion you see is a presentation layer. Any *value* jitter is a demo-only
 * device (see useLiveSimulator) — never shown against real backend data.
 */

// ── Timing ──────────────────────────────────────────────────────────────────
export const TICK_MS = 1500;          // synthetic value jitter cadence (demo only)
export const CLOCK_MS = 1000;         // live clock cadence
export const ROTATE_MS = 4500;        // recommendation ticker auto-advance
export const FLASH_EVERY_TICKS = 5;   // pulse one alert roughly every N ticks
export const FLASH_MS = 900;          // how long an alert flash lasts
export const MAX_POINTS = 48;         // sparkline ring-buffer cap

// Fraction of the value's full band used as the std-dev for synthetic jitter.
export const JITTER_SIGMA_FRAC = 0.012;

// ── Live status (derived from the alert's own bands) ─────────────────────────
// Distinct from the alert's stored `severity`: this is the *current* standing of
// the (possibly jittered) value, so the rail can show recovery to green.
export const LIVE_STATUS_META = {
  critical: { label: 'Critical', color: '#dc2626', bgLight: 'rgba(220,38,38,0.10)', bgDark: 'rgba(220,38,38,0.20)', border: 'rgba(220,38,38,0.32)' },
  warning:  { label: 'Warning',  color: '#d97706', bgLight: 'rgba(217,119,6,0.10)', bgDark: 'rgba(217,119,6,0.20)', border: 'rgba(217,119,6,0.32)' },
  ok:       { label: 'In Range', color: '#16a34a', bgLight: 'rgba(22,163,74,0.10)', bgDark: 'rgba(22,163,74,0.20)', border: 'rgba(22,163,74,0.30)' },
};

export function statusBg(meta, isDark) {
  return isDark ? meta.bgDark : meta.bgLight;
}

// ── Recommendation accents ───────────────────────────────────────────────────
// Local copies of the recommendation palette. The recommendations feature owns
// the canonical values, but feature folders can't import each other (ESLint
// `boundaries`), and this is a prototype — so we mirror the few hexes we need.
export const REC_ACCENT = '#f59e0b'; // amber lightbulb

// A "live" recommendation stack should surface only recs that still need an
// operator decision — not ones already acted on, rejected, or expired.
export const ACTIONABLE_REC_STATUSES = ['unacknowledged', 'accepted'];
export const REC_IMPACT_META = {
  high:   { label: 'High',   color: '#dc2626', bgLight: 'rgba(220,38,38,0.08)', bgDark: 'rgba(220,38,38,0.20)' },
  medium: { label: 'Medium', color: '#d97706', bgLight: 'rgba(217,119,6,0.08)', bgDark: 'rgba(217,119,6,0.20)' },
  low:    { label: 'Low',    color: '#2563eb', bgLight: 'rgba(37,99,235,0.08)', bgDark: 'rgba(37,99,235,0.20)' },
};

// "Acknowledged but not confirmed done" — the open operational loop. Our app is
// advisory and can't observe the DCS action, so an `accepted` rec stays pinned,
// neutrally phrased, until it reaches a terminal state elsewhere.
export const OPEN_LOOP_META = {
  color: '#0891b2', bgLight: 'rgba(8,145,178,0.10)', bgDark: 'rgba(8,145,178,0.22)',
  border: 'rgba(8,145,178,0.30)', label: 'Acknowledged — awaiting confirmation',
};

// ── Adaptive broadcast headline ──────────────────────────────────────────────
// The single most important real estate: one unambiguous top-level state,
// readable across the room. Deterministic precedence, highest wins:
//   DEGRADED → CRITICAL → ACTIONS WAITING → WATCHING → NOMINAL
// DEGRADED outranks CRITICAL on purpose: if we don't trust the inputs we must
// not assert "critical". ACTIONS outranks WATCHING: an action beats passive risk.
export const HEADLINE_META = {
  degraded: { state: 'degraded', label: 'ADVISORY PAUSED', icon: 'sensors_off',  color: '#0891b2', bgLight: 'rgba(8,145,178,0.12)',  bgDark: 'rgba(8,145,178,0.24)',  border: 'rgba(8,145,178,0.40)'  },
  critical: { state: 'critical', label: 'CRITICAL',        icon: 'crisis_alert', color: '#dc2626', bgLight: 'rgba(220,38,38,0.12)', bgDark: 'rgba(220,38,38,0.26)', border: 'rgba(220,38,38,0.42)'  },
  actions:  { state: 'actions',  label: 'ACTIONS WAITING',  icon: 'recommend',    color: '#d97706', bgLight: 'rgba(217,119,6,0.12)', bgDark: 'rgba(217,119,6,0.26)', border: 'rgba(217,119,6,0.42)'  },
  watching: { state: 'watching', label: 'WATCHING',         icon: 'visibility',   color: '#2563eb', bgLight: 'rgba(37,99,235,0.12)', bgDark: 'rgba(37,99,235,0.26)', border: 'rgba(37,99,235,0.42)'  },
  nominal:  { state: 'nominal',  label: 'ALL NOMINAL',      icon: 'check_circle', color: '#16a34a', bgLight: 'rgba(22,163,74,0.12)', bgDark: 'rgba(22,163,74,0.26)', border: 'rgba(22,163,74,0.34)'  },
};

/**
 * Resolve the broadcast headline from current counts. Pure + deterministic so
 * two operators glancing seconds apart read the same state.
 */
export function computeHeadline({ degraded = false, criticalCount = 0, actionsCount = 0, watchingCount = 0 } = {}) {
  if (degraded) return { ...HEADLINE_META.degraded, count: 0 };
  if (criticalCount > 0) return { ...HEADLINE_META.critical, count: criticalCount };
  if (actionsCount > 0) return { ...HEADLINE_META.actions, count: actionsCount };
  if (watchingCount > 0) return { ...HEADLINE_META.watching, count: watchingCount };
  return { ...HEADLINE_META.nominal, count: 0 };
}

// ── Ranking (replaces auto-rotation; a passive display must be glance-resumable) ──
export const REC_IMPACT_RANK = { high: 0, medium: 1, low: 2 };

/** Recs: critical-linked first → expected-impact desc → most-recent. */
export function rankRecs(recs = [], criticalRecIds = new Set()) {
  return [...recs].sort((a, b) => {
    const ca = criticalRecIds.has(a.id) ? 0 : 1;
    const cb = criticalRecIds.has(b.id) ? 0 : 1;
    if (ca !== cb) return ca - cb;
    const ia = REC_IMPACT_RANK[a.impact] ?? 3;
    const ib = REC_IMPACT_RANK[b.impact] ?? 3;
    if (ia !== ib) return ia - ib;
    return Date.parse(b.generatedAt ?? 0) - Date.parse(a.generatedAt ?? 0);
  });
}

// Alerts: severity → actionability → recency, with stale always sunk to the
// bottom. Within a severity tier, `predictive` (preventable) outranks an
// already-breached `ongoing` because prevention beats observing.
export const SEVERITY_RANK = { critical: 0, warning: 1 };
export const CATEGORY_ACTIONABILITY_RANK = { predictive: 0, just_off: 1, ongoing: 2, stale: 3 };

export function rankAlerts(alerts = []) {
  return [...alerts].sort((a, b) => {
    const sa = a.isStale ? 1 : 0;
    const sb = b.isStale ? 1 : 0;
    if (sa !== sb) return sa - sb;
    const va = SEVERITY_RANK[a.severity] ?? 2;
    const vb = SEVERITY_RANK[b.severity] ?? 2;
    if (va !== vb) return va - vb;
    const aa = CATEGORY_ACTIONABILITY_RANK[a.category] ?? 9;
    const ab = CATEGORY_ACTIONABILITY_RANK[b.category] ?? 9;
    if (aa !== ab) return aa - ab;
    return Date.parse(b.lastReading ?? 0) - Date.parse(a.lastReading ?? 0);
  });
}

// ── Data-freshness / trust ───────────────────────────────────────────────────
// The minimum trust contract: never present stale data as live fact. When too
// many inputs are stale we pause the advisory rather than reason on bad data.
export const DEGRADED_STALE_RATIO = 0.5;   // ≥ half the tracked inputs stale → paused
export const FRESHNESS_STALE_SECONDS = 90; // global feed considered stale past this age

export function isFeedDegraded(alerts = []) {
  if (!alerts.length) return false;
  const stale = alerts.filter((a) => a.isStale).length;
  return stale / alerts.length >= DEGRADED_STALE_RATIO;
}

/** Whole seconds between the data timestamp and the wall clock (never negative). */
export function freshnessAgeSeconds(dataAsOf, now) {
  if (!dataAsOf || !now) return 0;
  return Math.max(0, Math.round((now.getTime() - dataAsOf.getTime()) / 1000));
}

/**
 * Lower/upper bound the value can realistically swing to. Prefer the critical
 * band; fall back to a widened optimal band; finally a ±10% window around the
 * value so a band-less metric still moves a little.
 */
export function valueBounds(a) {
  const lo =
    a.criticalMin != null ? a.criticalMin
    : a.optimalMin != null ? a.optimalMin * 0.9
    : a.value * 0.9;
  const hi =
    a.criticalMax != null ? a.criticalMax
    : a.optimalMax != null ? a.optimalMax * 1.1
    : a.value * 1.1;
  return { lo, hi };
}

/**
 * Current standing of a value against its bands → 'critical' | 'warning' | 'ok'.
 */
export function computeStatus(value, a) {
  if ((a.criticalMin != null && value < a.criticalMin) ||
      (a.criticalMax != null && value > a.criticalMax)) {
    return 'critical';
  }
  if ((a.optimalMin != null && value < a.optimalMin) ||
      (a.optimalMax != null && value > a.optimalMax)) {
    return 'warning';
  }
  return 'ok';
}

// Friendly labels for alert categories (local mirror — see note above).
export const CATEGORY_LABEL = {
  just_off: 'Just off range',
  ongoing: 'Sustained breach',
  predictive: 'Early warning',
  stale: 'Stale data',
};

/** Standard-normal sample via Box-Muller. App code only — never in workflows. */
export function gaussian() {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

export function clamp(x, lo, hi) {
  return Math.min(hi, Math.max(lo, x));
}

// Append a 2-digit hex alpha safely. Expands #rgb -> #rrggbb first, so a short
// hex like '#fff' doesn't produce an invalid '#fff33' (which crashes chart.js
// gradient color stops). Non-hex colors pass through untouched.
export function withAlpha(color, alpha) {
  let c = color;
  if (/^#[0-9a-fA-F]{3}$/.test(c)) {
    c = '#' + c.slice(1).split('').map((ch) => ch + ch).join('');
  }
  return /^#[0-9a-fA-F]{6}$/.test(c) ? c + alpha : c;
}

// ── Furnace SVG selection ────────────────────────────────────────────────────
// The product's primary plant is a blast furnace; that's the only hero shipped
// in main today. Map deployment "type" to a same-origin /public SVG, defaulting
// to the blast furnace so the hero always renders.
const SVG_BY_TYPE = {
  blast_furnace: '/svg-templates/blastfurnace_v2.svg',
  power_plant: '/svg-templates/powerplant.svg',
  cement_kiln: '/svg-templates/cementkiln.svg',
  cfbc_boiler: '/svg-templates/cfbcboiler.svg',
  aluminium_potline: '/svg-templates/aluminiumpotline.svg',
  zinc_cellhouse: '/svg-templates/zinccellhouse.svg',
};

export const DEFAULT_FURNACE_SVG = '/svg-templates/blastfurnace_v2.svg';

export function furnaceSvgUrl(plantType) {
  if (!plantType) return DEFAULT_FURNACE_SVG;
  const key = String(plantType).trim().toLowerCase().replace(/\s+/g, '_');
  return SVG_BY_TYPE[key] ?? DEFAULT_FURNACE_SVG;
}
